import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import StyledSelect from '../../../components/forms/styledSelect';
import { useQuery, useQueryClient } from 'react-query';
import DatePicker from '../../../components/forms/formDatePicker';
import AssigneeSelect from '../../../components/selects/assigneeSelect';
import BadgeSelect from '../../../components/selects/badgeSelect';
import ServiceCategorySelect from '../../../components/selects/serviceCategorySelect';
import ServiceSelect from '../../../components/selects/serviceSelect';
import { getBadges } from '../../../services/badges';
import { getUsersAsOptions } from '../../../services/companyUsers';
import { onQuerySuccess } from '../../../utils/queryResponse';
import ClientsSelect from '../../../components/selects/clientsSelect';
import { useSearchParams } from 'react-router-dom';
import FormInput from '../../../components/forms/formInput';
import { getStatusOptions } from '../../../constants/jobStatus';

const FilterIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
);
const ChevIcon = () => (
    <svg className="ic chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const JobReportsFilter = ({ isFiltering, onFilter }) => {

    const [searchParams] = useSearchParams();

    const [isShow, setIsShow] = useState(searchParams.size ? true : false)
    const { t } = useTranslation();

    const queryClient = useQueryClient()
    const [assigneesOptions, setAssigneesOptions] = useState([])
    const [badgesOptions, setBadgesOptions] = useState([])
    const [clientOptions, setClientOptions] = useState([])

    // Filters
    const [status, setStatus] = useState('')
    const [paymentStatus, setPaymentStatus] = useState('')
    // const [pickupTimeStartDate, setPickupTimeStartDate] = useState('')
    // const [pickupTimeEndDate, setPickupTimeEndDate] = useState('')
    // const [completionTimeStartDate, setCompletionTimeStartDate] = useState('')
    // const [completionTimeEndDate, setCompletionTimeEndDate] = useState('')
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const [pickupAssignedToId, setPickupAssignedToId] = useState(searchParams.get("pickupAssignedToId"))
    const [cleaningAssignedToId, setCleaningAssignedToId] = useState(searchParams.get("cleaningAssignedToId"))
    const [deliveryAssignedToId, setDeliveryAssignedToId] = useState(searchParams.get("deliveryAssignedToId"))
    const [serviceId, setServiceId] = useState(searchParams.get("serviceId"))
    const [serviceCategoryId, setServiceCategoryId] = useState('')
    const [badgeId, setBadgeId] = useState('')
    const [clientId, setClientId] = useState('')
    const [jobNoFrom, setJobNoFrom] = useState('')
    const [jobNoTo, setJobNoTo] = useState('')

    const activeCount = [
        status, paymentStatus, dateFrom, dateTo, pickupAssignedToId, cleaningAssignedToId,
        deliveryAssignedToId, serviceId, serviceCategoryId, badgeId, clientId, jobNoFrom, jobNoTo
    ].filter(Boolean).length

    const { data: dbAssigneesOptions, isLoading: isLoadingAssigneesOptions } = useQuery(['users-option'], () => getUsersAsOptions())

    useEffect(() => {
        onQuerySuccess(dbAssigneesOptions, (result) => {
            if (result.length > 0) {
                setAssigneesOptions(result.map(d => ({
                    value: d.id, label: d.fullName, avatarUrl: d.avatarUrl
                })))
            }
        })
    }, [dbAssigneesOptions, isShow])

    const { data, isBadgesLoading } = useQuery(['badges'], () => getBadges())

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            if (result.length > 0) {
                setBadgesOptions(result.map(d => ({
                    value: d.id, label: d.badgeNo
                })))
            }
        })
    }, [data])

    const handleFilter = (e) => {
        e.preventDefault()
        e.stopPropagation()
        onFilter([
            status,
            paymentStatus,
            // pickupTimeStartDate,
            // pickupTimeEndDate ? new Date(pickupTimeEndDate.setHours(23, 59, 59, 999)) : '',
            // completionTimeStartDate,
            // completionTimeEndDate ? new Date(completionTimeEndDate.setHours(23, 59, 59, 999)) : '',
            dateFrom,
            dateTo ? new Date(dateTo.setHours(23, 59, 59, 999)) : '',
            pickupAssignedToId,
            cleaningAssignedToId,
            deliveryAssignedToId,
            serviceId,
            serviceCategoryId,
            badgeId,
            clientId,
            jobNoFrom,
            jobNoTo
        ])
    }

    const handleReset = () => {
        onFilter(['', '', '', '', '', '', '', '', '', '', '', '', '', ''])
        setStatus('')
        setPaymentStatus('')
        // setPickupTimeStartDate('')
        // setPickupTimeEndDate('')
        // setCompletionTimeStartDate('')
        // setCompletionTimeEndDate('')
        setDateFrom('')
        setDateTo('')
        setPickupAssignedToId('')
        setCleaningAssignedToId('')
        setDeliveryAssignedToId('')
        setServiceId('')
        setServiceCategoryId('')
        setBadgeId('')
        setClientId('')
        setJobNoFrom('')
        setJobNoTo('')
        queryClient.invalidateQueries(['job-reports'])
    }

    const handleStatusChange = (val) => {
        setStatus(val)
    }

    return (
        <>
            <button type="button" className={`filter-toggle${isShow ? ' on' : ''}`} onClick={() => setIsShow(s => !s)}>
                <FilterIcon /> {t('reports.filters')}
                {activeCount > 0 && <span className="cnt">{activeCount}</span>}
                <ChevIcon />
            </button>

            {isShow && (
                <Form onSubmit={handleFilter} className="filter-panel">
                    <div className="filter-grid">
                        <div>
                            <Form.Label>{t('reports.serviceFilter')}</Form.Label>
                            <ServiceSelect value={serviceId} onChange={(option) => setServiceId(option.value)} />
                        </div>
                        <div>
                            <ServiceCategorySelect label={t('reports.serviceCategoryFilter')} placeholder={t('reports.filterServiceCategory')} value={serviceCategoryId} onChange={(option) => setServiceCategoryId(option.value)} />
                        </div>
                        <Form.Group>
                            <Form.Label>{t('reports.clientFilter')}</Form.Label>
                            <ClientsSelect value={clientId} onChange={(option) => setClientId(option.value)} options={clientOptions} setOptions={setClientOptions} />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>{t('reports.tagNumber')}</Form.Label>
                            <BadgeSelect value={badgeId}
                                isLoading={isBadgesLoading}
                                options={badgesOptions.sort(function (a, b) { return a.label - b.label })}
                                onChange={(option) => setBadgeId(option.value)} />
                        </Form.Group>
                        <Form.Group>
                            <AssigneeSelect
                                label={t('reports.pickedUpBy')}
                                value={pickupAssignedToId}
                                assigneesOptions={assigneesOptions}
                                onChange={(option) => setPickupAssignedToId(option.value)}
                                isLoading={isLoadingAssigneesOptions}
                            />
                        </Form.Group>
                        <Form.Group>
                            <AssigneeSelect
                                label={t('reports.cleanedBy')}
                                value={cleaningAssignedToId}
                                assigneesOptions={assigneesOptions}
                                onChange={(option) => setCleaningAssignedToId(option.value)}
                                isLoading={isLoadingAssigneesOptions}
                            />
                        </Form.Group>
                        <Form.Group>
                            <AssigneeSelect
                                label={t('reports.deliveredBy')}
                                value={deliveryAssignedToId}
                                assigneesOptions={assigneesOptions}
                                onChange={(option) => setDeliveryAssignedToId(option.value)}
                                isLoading={isLoadingAssigneesOptions}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>{t('reports.paymentStatusFilter')}</Form.Label>
                            <StyledSelect value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} >
                                <option value="" disabled>{t('reports.selectStatus')}</option>
                                <option value="PENDING">{t('reports.pendingOption')}</option>
                                <option value="PAID">{t('reports.paidOption')}</option>
                            </StyledSelect>
                        </Form.Group>
                        {/* <Col lg="3" className='mb-2'>
                            <DatePicker className='form-control' label='Pickup Start Date'
                                selected={pickupTimeStartDate} onChange={setPickupTimeStartDate} maxDate={pickupTimeEndDate} dateFormat="dd.MM.yyyy"
                            />
                        </Col>
                        <Col lg="3" className='mb-2'>
                            <DatePicker className='form-control' label='Pickup End Date'
                                selected={pickupTimeEndDate} onChange={setPickupTimeEndDate} minDate={pickupTimeStartDate} dateFormat="dd.MM.yyyy"
                            />
                        </Col>
                        <Col lg="3" className='mb-2'>
                            <DatePicker className='form-control' label='Completion Start Date'
                                selected={completionTimeStartDate} onChange={setCompletionTimeStartDate} maxDate={completionTimeEndDate} dateFormat="dd.MM.yyyy"
                            />
                        </Col>
                        <Col lg="3" className='mb-2'>
                            <DatePicker className='form-control' label='Completion End Date'
                                selected={completionTimeEndDate} onChange={setCompletionTimeEndDate} minDate={completionTimeStartDate} dateFormat="dd.MM.yyyy"
                            />
                        </Col> */}
                        <FormInput label={t('reports.from')} placeholder={t('reports.jobNumber')} value={jobNoFrom} onChange={(e) => setJobNoFrom(e.target.value)} />
                        <FormInput label={t('reports.to')} placeholder={t('reports.jobNumber')} value={jobNoTo} onChange={(e) => setJobNoTo(e.target.value)} />
                        <Form.Group>
                            <Form.Label>{t('reports.statusFilter')}</Form.Label>
                            <StyledSelect value={status} onChange={(e) => handleStatusChange(e.target.value)} >
                                <option value="" disabled>{t('reports.selectStatus')}</option>
                                {getStatusOptions().map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </StyledSelect>
                        </Form.Group>
                        {
                            status &&
                            <>
                                <DatePicker className='form-control' label={t('reports.dateFrom')}
                                    selected={dateFrom} onChange={setDateFrom} maxDate={dateTo} dateFormat="dd.MM.yyyy"
                                />
                                <DatePicker className='form-control' label={t('reports.dateTo')}
                                    selected={dateTo} onChange={setDateTo} minDate={dateFrom} dateFormat="dd.MM.yyyy"
                                />
                            </>
                        }
                    </div>

                    <div className="filter-foot">
                        <span className="meta">
                            {activeCount > 0 ? t('reports.filtersSelected', { count: activeCount }) : t('reports.noFiltersApplied')}
                        </span>
                        <div className="sp">
                            <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={handleReset}>{t('reports.reset')}</button>
                            <button type="submit" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={isFiltering}>
                                {isFiltering ? t('reports.filtering') : t('reports.applyFilters')}
                            </button>
                        </div>
                    </div>
                </Form>
            )}
        </>
    )
}

export default JobReportsFilter
